import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import {
  Box,
  Button,
  Divider,
  Link,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import { THOR_COLORS } from '@/theme/thorTheme';
import { BaseCard } from '@/components/BaseCard';
import Logo from '@/components/Logo';
export default function Home() {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const linkSx: SxProps = {
    fontWeight: 'bold',
    display: 'block',
    color: `${THOR_COLORS.cream} !important`,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(255,255,255,0.3)',
    textAlign: 'center',
  };

  const buttonSx: SxProps = {
    display: 'block',
    color: `${THOR_COLORS.lightGreen} !important`,
    borderColor: THOR_COLORS.lightGreen,
    '&:hover': {
      backgroundColor: THOR_COLORS.lightGreen,
      color: THOR_COLORS.darkGreen,
    },
  };

  return (
    <Stack
      direction="column"
      sx={{
        width: '100%',
        alignItems: 'center',
        height: '100vh',
        display: 'flex',
        backgroundImage: 'url(/img/landing_page_bg.png)',
        backgroundSize: { xs: '100% auto', sm: 'cover' },
        backgroundPosition: 'top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', sm: 'fixed' },
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: '100%',
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 5, md: 0 },
          mb: { xs: 5, md: 0 },
        }}
      ></Stack>
      {/* Main Content */}

      <Stack
        direction="column"
        spacing={3}
        sx={{
          alignItems: 'center',
          backgroundColor: THOR_COLORS.darkGreen,
          opacity: 0.9,
          color: THOR_COLORS.cream,
          padding: { xs: '24px', md: '36px 48px' },
          width: '100%',
          maxWidth: '1200px',
          height: 'auto',
          borderRadius: isSmallScreen ? 0 : '4px',
          margin: '0 auto',
        }}
      >
        <Logo />
        {/* Intro Text */}
        <Typography
          variant="body1"
          color={THOR_COLORS.cream}
          sx={{
            textAlign: 'center',
            width: { xs: '100%', md: '80%' },
            mt: 0,
            maxWidth: 740,
            fontSize: '1rem',
          }}
        >
          We lead cutting-edge research to develop the next generation of intelligent robots, safely
          trained in advanced simulation environments to automate routine tasks and improve daily
          life. All of our discoveries are open sourced, enabling the community to build on our
          progress and collaboratively shape the future of embodied AI
        </Typography>

        {/* Featured Viewer Configs */}
        <Stack
          sx={{
            width: '100%',
          }}
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr', // mobile
                md: 'repeat(3, 1fr)', // tablet
              },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <BaseCard
              imageUrl="img/manip.png"
              imageAlt="Molmo-Manip"
              title="Molmo-Manip"
              description="Advancing models for robotics"
              onClick={() => {
                navigate('/molmo-manip');
              }}
            />
            <BaseCard
              imageUrl="img/benchmark.png"
              imageAlt="Benchmark & Leaderboards"
              title="Benchmark & Leaderboards"
              description="Benchmarking the performance of our models"
              onClick={() => {
                navigate('/leaderboard');
              }}
            />
            <BaseCard
              imageUrl="https://www.datocms-assets.com/64837/1743546076-environmentsimage.jpg?fit=fill&fm=webp&h=639&w=705"
              imageAlt="Environments"
              title="Environments"
              description="Training at massive scale in simulations"
              onClick={() => {
                navigate('/environment');
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{
            pt: 2,
          }}
        >
          <Button
            target="_blank"
            rel="noopener"
            href="https://allenai.org/embodied-ai"
            variant="outlined"
            sx={buttonSx}
            data-cy="learn-more"
          >
            About Embodied AI{' '}
            <ArrowOutwardIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} />
          </Button>
        </Stack>
      </Stack>

      {/* Footer Links */}
      <Stack
        direction="row"
        spacing={1}
        flex={1}
        justifyContent="center"
        alignItems="flex-end"
        sx={{
          backgroundColor: { xs: THOR_COLORS.darkGreen, md: 'transparent' },
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ pb: 3 }}>
          <Link
            target="_blank"
            rel="noopener"
            href="https://allenai.org/privacy-policy/2025-02-19"
            sx={{
              ...linkSx,
              fontWeight: 'normal',
            }}
            data-cy="privacy-policy"
          >
            Privacy policy
          </Link>
          <Divider orientation="vertical" flexItem sx={{ backgroundColor: THOR_COLORS.cream }} />
          <Link
            target="_blank"
            rel="noopener"
            href="https://allenai.org/terms/2024-09-25"
            sx={{
              ...linkSx,
              fontWeight: 'normal',
            }}
            data-cy="terms-of-use"
          >
            Terms of use
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
}
